import * as React from 'react';
import styles from './EasterEgg.scss';
import { useSelector } from 'react-redux';
import equal from 'fast-deep-equal/react';
import { useDispatch } from 'react-redux';
import { POPUPS_NAME } from '~/components/PopupManager';
import classNames from 'classnames';
import PreCacheImage from '~/utils/preCacheImage';
import { Howl } from 'howler';
import { getCDNUrl, getStaticUrl } from '~/utils/utils';
import { RootState } from '~/Store';
import { appActions } from '~/Store/appSlice';
import { CATEGORIES } from '~/const';

interface IStateSelector {
    gifts: IGift[];
    currentPage: ICurrentPage;
    categories: ICategories;
    isBlurView: boolean;
}

const stateSelector = (state: RootState): IStateSelector => {
    return {
        gifts: state.account.gifts,
        currentPage: state.app.currentPage,
        categories: state.app.categories,
        isBlurView: state.app.isBlurView,
    };
};

const messUpValues = (values: number[]) => {
    return values.sort(() => Math.random() - 0.5);
};

const CORRECT_SEQUENCE = messUpValues([1, 2, 3, 4, 5]);

const soundsMap: Record<number | string, Howl> = {
    [CORRECT_SEQUENCE[0]]: new Howl({ src: getCDNUrl('armory/1411/easter/Armory_showcase_Melody_Part_1.mp3', true) }),
    [CORRECT_SEQUENCE[1]]: new Howl({ src: getCDNUrl('armory/1411/easter/Armory_showcase_Melody_Part_2.mp3', true) }),
    [CORRECT_SEQUENCE[2]]: new Howl({ src: getCDNUrl('armory/1411/easter/Armory_showcase_Melody_Part_3.mp3', true) }),
    [CORRECT_SEQUENCE[3]]: new Howl({ src: getCDNUrl('armory/1411/easter/Armory_showcase_Melody_Part_4.mp3', true) }),
    [CORRECT_SEQUENCE[4]]: new Howl({ src: getCDNUrl('armory/1411/easter/Armory_showcase_Melody_Part_5.mp3', true) }),
    final: new Howl({ src: getCDNUrl('armory/1411/easter/Armory_showcase_Melody_Full.mp3', true) }),
};

const socksImages: Record<number, string> = {
    1: getStaticUrl(require('./images/Christmas-egg_1.png')),
    2: getStaticUrl(require('./images/Christmas-egg_2.png')),
    3: getStaticUrl(require('./images/Christmas-egg_3.png')),
    4: getStaticUrl(require('./images/Christmas-egg_4.png')),
    5: getStaticUrl(require('./images/Christmas-egg_5.png')),
};

const easterSvgPaths: Record<number, Record<'shape' | 'activeZone', string>> = {
    1: {
        shape: 'M535 342L545.5 319L551 322.5L561.5 330.5C564.5 333 569.5 338.5 572 342C574 344.5 573.667 348.833 572.5 350.5L561.5 368L560 370.5L558 371.5L545 399.5C543.5 402.5 541.8 407.6 543 416C545 422.5 549 428.5 549 437.5C549.4 453.9 542 454.5 536 453.5L529.5 449.5L525.5 443L522.5 436.5L520.5 429.5L518 422L515.5 416.5L513 413.5L506 406L505 403.5V400L510 392L515 384L535 346V342Z',
        activeZone:
            'M527 321.5L543 302.5L563 310L577 319.5C580 322 584.5 331 587 334.5C589 337 587.667 355.333 586.5 357L573.5 379L572 381.5L570 382.5L562 400C560.5 403 560.8 411.6 562 420C564 426.5 565 437 565 446C565.4 462.4 541 475 535 474L512 455L509 443L504.5 436V429.5L502 422L493.5 412.5L491 409.5V404.5L493 399.5V396L500 377.5L509 365L521 336.5L527 321.5Z',
    },
    2: {
        shape: 'M643.5 376.5C645.9 371.7 646.167 350.833 646 341C657 337 653.5 330 657 326.5C665 329 681.5 334 683 341C682.5 352.5 678 367 674.5 369C671 376.5 673.612 379 670 387.5C666.5 399.5 668 404 665 411.5C662.5 420 682 428.5 681 443C678.6 460.6 667 455 661.5 450C656.5 444 651.5 435.5 645 428.5C636.5 423 634 418.5 631 412.5C630.5 405.5 636.5 396 641 391C641 389.5 639.5 389.214 642 386C645 382.143 641.1 381.3 643.5 376.5Z',
        activeZone:
            'M622.5 376.5C624.9 371.7 635.167 336.833 635 327C646 323 642.5 316 646 312.5C654 315 697.5 320 699 327C698.5 338.5 697 367 693.5 369C690 376.5 692.612 379 689 387.5C685.5 399.5 688 404 685 411.5C682.5 420 699 428.5 698 443C701.5 475 669 485.6 648.5 461C643.5 455 638.5 446.5 632 439.5C623.5 434 618 429.5 615 423.5C614.5 416.5 615.5 396 620 391C620 389.5 618.5 389.214 621 386C624 382.143 620.1 381.3 622.5 376.5Z',
    },
    3: {
        shape: 'M733 270C734.6 267.2 734.5 262 734.5 259C737.5 259.5 741 262 746.5 261.5C760 263 764.334 268.833 767 273V280.5L764.5 283.5C765.5 283.5 766.5 285 766 286C765.2 289.6 764.667 295.5 764.5 298L763.5 299L759.5 324.5C759 330.5 757.7 342 758.5 346C759.1 349 774 356.5 776.5 369.5C778.5 387.5 767.5 385.5 762 383.5C756 382 741.5 367 738 363.5C735.5 360.5 732 361 727.5 356.5C725 355 725 350.665 726 346C727.5 339 731 334.5 733 327C732.5 313 733.5 300.5 734.5 292L733 291C733 284.5 732.5 275.5 733 270Z',
        activeZone:
            'M721 270C722.6 267.2 722.5 262 722.5 259C727 247.5 741 248 746.5 247.5C760 249 778.334 268.833 781 273V280.5L778.5 283.5C779.5 283.5 780.5 285 780 286C779.2 289.6 778.667 295.5 778.5 298L777.5 299L773.5 324.5C773 330.5 775.7 342 776.5 346C777.1 349 785 356.5 787.5 369.5C789.5 387.5 777.5 401.375 762 397.5C756 396 728.5 378 725 374.5C722.5 371.5 719 367 714.5 362.5C712 361 712 353.665 713 349C714.5 342 719 334.5 721 327C720.5 313 721.5 300.5 722.5 292L721 291C721 284.5 720.5 275.5 721 270Z',
    },
    4: {
        shape: 'M827.5 363C827.498 356 829 341 831.5 335.5C833.333 335.5 837.4 335.6 839 336C841 336.5 848.5 340 855 344C859.5 346.5 862 348 861 352C860 353 860 355 859 361C859.5 365 857 363.5 857 367C857 373.4 854.667 377 853.5 378C852 385.833 847.828 401.218 847.5 404.5C847 409.5 845 421 848 425C850.5 432.5 858.5 437 859.5 445C860.5 460.5 852.5 461.5 843 456C837 453 835 443 826.5 434.5C817 429 814 423.5 813 419C812.5 411.5 823.5 401 823 395C825 388.5 826.333 375 828 365C828 364 827.5 363.5 827.5 363Z',
        activeZone:
            'M813.5 363C813.499 356 815 341 817.5 335.5C825.681 317.5 837.4 320.6 839 321C841 321.5 848.5 323 855 327C859.5 329.5 878 338.5 875 352C874 353 874 355 873 361C873.5 365 871 363.5 871 367C871 373.4 868.667 377 867.5 378C866 385.833 864.828 401.218 864.5 404.5C864 409.5 862.5 418 865.5 422C868 429.5 872.5 437 873.5 445C874.5 460.5 862 481.5 837 469C831 466 829 456 820.5 447.5C811 442 806 436.5 805 432C795.5 412 809.5 401 809 395C811 388.5 812.333 375 814 365C814 364 813.5 363.5 813.5 363Z',
    },
    5: {
        shape: 'M912 380.5C920 369 927.667 347.5 931.5 336C930.5 335 930 335.5 930 333.5C933 326.5 932.5 314.546 941 313C946.5 312 959.167 324.167 965 331L956.5 351L953 353.5L944 379C942.5 382.667 939.4 391.3 939 396.5C941.5 402.5 948.5 411 949 418.5C951 441 934 435.5 927 424.5C924.5 418.5 919.5 407.5 910.5 397.5C906.45 393 905.6 389.3 912 380.5Z',
        activeZone:
            'M896 380.5C904 369 912.667 347.5 916.5 336C915.5 335 915 335.5 915 333.5C918 326.5 927.5 298.546 936 297C948.5 294.727 974 313 979 328L973.5 351L970 353.5L959 379C957.5 382.667 954.4 391.3 954 396.5C956.5 402.5 961.5 414 962 421.5C964 444 947 452.5 925 443.5C922.5 437.5 905.5 414.5 897.5 397.5C893.45 393 889.6 389.3 896 380.5Z',
    },
};

const EasterEgg = () => {
    const dispatch = useDispatch();
    const { gifts, currentPage, categories, isBlurView } = useSelector<RootState, IStateSelector>(stateSelector, equal);
    const secretGift = gifts?.find((gift) => gift.isSecretGift);
    const [cookiesFounded, setCookies] = React.useState([]);
    const [isIncorrect, toggleInCorrect] = React.useState(false);
    const [isAnimate, startAnimation] = React.useState(false);
    const timeout = React.useRef<NodeJS.Timeout>();
    const activeHowl = React.useRef<Howl>(null);

    const onClickHandler = React.useCallback(
        (event: React.MouseEvent) => {
            const target = event.target as HTMLElement;
            const index = target.dataset.index;
            if (!index || cookiesFounded.includes(+index)) {
                return;
            }

            if (activeHowl.current) {
                activeHowl.current.stop();
            }

            soundsMap[index].play();
            activeHowl.current = soundsMap[index];

            const sequence = [...cookiesFounded, +index];
            const isCorrect = sequence.every((value, index) => value === CORRECT_SEQUENCE[index]);

            if (isCorrect && sequence.length === CORRECT_SEQUENCE.length) {
                startAnimation(true);
                activeHowl.current.stop();
                soundsMap.final.play();
                setTimeout(() => {
                    dispatch(
                        appActions.changeVisibilityPopup({
                            name: POPUPS_NAME.GIFT,
                            isVisible: true,
                            data: { gift: secretGift, showRewards: true },
                        }),
                    );
                    startAnimation(false);
                    setCookies([]);
                }, 2300);
            } else if (isCorrect) {
                setCookies(sequence);
            } else {
                toggleInCorrect(true);
                timeout.current = setTimeout(() => {
                    clearTimeout(timeout.current);
                    setCookies([]);
                    toggleInCorrect(false);
                }, 500);
            }
        },
        [cookiesFounded, secretGift],
    );

    const onMouseEnter = (event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        const index = target.dataset.index;
        if (index && !(target.parentNode.parentNode as HTMLDivElement).classList.contains(styles.correct)) {
            target.parentNode.children[0].classList.add(styles.hover);
        }
    };

    const onMouseLeave = (event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        const index = target.dataset.index;
        if (index) {
            target.parentNode.children[0].classList.remove(styles.hover);
        }
    };

    const isSanta = currentPage?.name === CATEGORIES.SANTA_CONTAINERS;
    const isDisabledEaster = !secretGift || currentPage?.isBundlePage || !isSanta;

    React.useEffect(() => {
        if (!secretGift && soundsMap.final.playing()) {
            soundsMap.final.stop();
        }
    }, [secretGift]);

    React.useEffect(() => {
        return () => {
            clearTimeout(timeout.current);

            if (activeHowl.current) {
                activeHowl.current.stop();
            }
        };
    }, []);

    React.useEffect(() => {
        setCookies([]);
        toggleInCorrect(false);
    }, [currentPage]);

    if (isDisabledEaster) {
        return null;
    }

    PreCacheImage.add(secretGift.icons.innerBackground);

    const classesWrapper = classNames(styles.wrapper, {
        [styles.animate]: isAnimate,
        [styles.isBlur]: isBlurView,
    });

    return (
        <div className={classesWrapper} onClick={onClickHandler}>
            <div className={styles.content}>
                {CORRECT_SEQUENCE.map((value: number, index) => {
                    return (
                        <div
                            className={classNames(styles.easterItem, styles[`item${value}`], {
                                [styles.correct]: cookiesFounded.includes(value),
                                [styles.incorrect]: isIncorrect,
                            })}
                        >
                            <svg viewBox="0 0 2560 1440" preserveAspectRatio="xMidYMid slice" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.svgWrapper}>
                                <path d={easterSvgPaths[value].shape} />
                                <path data-index={value} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} d={easterSvgPaths[value].activeZone} />
                            </svg>
                            <img src={socksImages[index + 1]} className={classNames(styles.smallItem, styles[`smallItem${index + 1}`], styles[`anim${value}`])} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default React.memo(EasterEgg);
